import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HealthcareEntitiesService } from '../../../../../js/api';
import type { FacilityList } from '../../../../../js/api/models/FacilityList';
import { FacilityTypeEnum } from '../../../../../js/api/models/FacilityTypeEnum';
import { SessionContext, SessionContextType } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { healthcareEntityFacilityQueryKeys } from '../../../../../js/app/query-keys-factories/facilities';
import { Button } from '../../../components/button';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { AddNewFacilityDialog } from '../AddNewFacilityDialog';
import { FacilityDrawer } from '../FacilityDrawer';
import FacilityCard from './FacilityCard';
import { FACILITIES_LIST_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 21;
const DATE_FORMAT = 'LLL d, yyyy';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FacilitiesList: React.FC = () => {
  useDocumentTitle('Facilities');
  const queryClient = useQueryClient();
  const { user } = useContext<SessionContextType>(SessionContext);
  const canEditFacility = Boolean(user?.group_names?.includes('manage_facilities'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const displayTypePrefKey = 'OrganizationFacilities';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: [],
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        categories: filterValues.categories,
        displayType,
        ordering: next,
      });
    },
    [updateFilters, filterValues.search, filterValues.categories, displayType]
  );

  const queryKey = useMemo(() => [FACILITIES_LIST_QUERY_KEY, search, ordering], [search, ordering]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return HealthcareEntitiesService.healthcareEntitiesFacilitiesList({
        search: search || undefined,
        ordering: ordering as Parameters<
          typeof HealthcareEntitiesService.healthcareEntitiesFacilitiesList
        >[0]['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
  });

  const facilities = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreFacilities = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreFacilities, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(['FacilityTypes', 'EMRChoices']);
  const facilityTypeMap = codeDefinitionMaps?.FacilityTypes || {};
  const emrMap = codeDefinitionMaps?.EMRChoices || {};

  useEffect(() => {
    const facilityIdFromUrl = searchParams.get('id');
    if (facilityIdFromUrl) {
      setIsDrawerOpen(true);
    } else {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (facility: FacilityList) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', facility.id);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedIdFromUrl = searchParams.get('id');

  const handleAddFacility = async (data: { name: string; type: string }) => {
    try {
      const response = await HealthcareEntitiesService.healthcareEntitiesFacilitiesCreate({
        requestBody: {
          name: data.name,
          facility_type: data.type as FacilityTypeEnum,
        },
      });
      toast.success('Facility created successfully');
      setIsDialogOpen(false);
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
      queryClient.invalidateQueries({ queryKey: [FACILITIES_LIST_QUERY_KEY] });
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', response.id);
      setSearchParams(newSearchParams, { replace: true });
    } catch (error) {
      console.error('Failed to create facility:', error);
      toast.error('Failed to create facility. Please try again.');
    }
  };

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0 tw:dark:!text-zinc-100">
                  <i className="far fa-fw fa-hospital tw:mr-1" />
                  Facilities
                </Heading>
                {canEditFacility && (
                  <Button
                    color="brandgreen1"
                    onClick={() => setIsDialogOpen(true)}
                    className="ms-3"
                  >
                    Add Facility
                  </Button>
                )}
              </div>

              <div className="tw:mt-1 tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  categories={[]}
                  showStatus={false}
                  showReason={false}
                  showDisplayType={true}
                  sortOptions={[
                    { label: 'Name', value: 'name' },
                    { label: 'City', value: 'city' },
                    { label: 'EMR', value: 'emr' },
                    { label: 'Facility Type', value: 'facility_type' },
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'created_at' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>
            <div className="tw:mt-3">
              {isLoading && facilities.length === 0 && (
                <div className="tw:text-center tw:py-8">Loading facilities...</div>
              )}
              {!isLoading && facilities.length === 0 ? (
                <div className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8">
                  No facilities found.
                  <div className="tw:text-zinc-500 tw:italic tw:text-sm">
                    Refine your search or add a new facility.
                  </div>
                </div>
              ) : (
                <>
                  {facilities.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {facilities.length} of {totalCount} facilities
                    </div>
                  )}

                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {facilities.map((facility) => {
                          const isSelected = facility.id === selectedIdFromUrl;
                          return (
                            <div
                              key={facility.id}
                              onClick={() => handleRowClick(facility)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleRowClick(facility);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <FacilityCard
                                facility={facility}
                                facilityTypeMap={facilityTypeMap}
                                emrMap={emrMap}
                                isSelected={isSelected}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <Table striped dense>
                      <TableHead>
                        <TableSortHeader
                          sortKey="name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Name
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="city"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Location
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="facility_type"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Facility Type
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="emr"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          EMR
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="updated_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Updated
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="created_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Created
                        </TableSortHeader>
                      </TableHead>
                      <TableBody>
                        {facilities.map((facility) => {
                          const isSelected = facility.id === selectedIdFromUrl;
                          return (
                            <TableRow
                              key={facility.id}
                              onClick={() => handleRowClick(facility)}
                              className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                            >
                              <TableCell>{facility.name}</TableCell>
                              <TableCell>
                                {getFacilityLocationDisplayValue(facility) || '--'}
                              </TableCell>
                              <TableCell>
                                {facilityTypeMap[facility.facility_type] || facility.facility_type}
                              </TableCell>
                              <TableCell>
                                {facility.emr ? emrMap[facility.emr] || facility.emr : '--'}
                              </TableCell>
                              <TableCell>
                                {facility.updated_at
                                  ? format(parseISO(facility.updated_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {facility.created_at
                                  ? format(parseISO(facility.created_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more facilities...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <FacilityDrawer
                key={selectedIdFromUrl}
                facilityId={selectedIdFromUrl}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>

      {canEditFacility && (
        <AddNewFacilityDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleAddFacility}
        />
      )}
    </>
  );
};
